import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkle, Flame } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

export const CheckinCard = ({ onPickPrompt }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/checkins/me");
      setData(res.data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { load(); }, []);

  const pick = async (mood) => {
    setSaving(true);
    try {
      const res = await api.post("/checkins", { mood });
      setData(res.data);
      toast.success("Mood saved. Thanks for checking in 🌿");
    } catch {
      toast.error("Could not save your check-in");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-gradient-to-br from-[#EAE6D9] to-[#F4F1E8] rounded-[2rem] p-6 sm:p-7 border border-[#E6EBE4]/60 mb-6"
      data-testid="checkin-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" color="#8A9A86" />
          <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Daily check-in</span>
        </div>
        {data.streak > 0 && (
          <div className="flex items-center gap-1.5 bg-white/70 rounded-full px-3 py-1.5 text-sm font-semibold text-[#D48A54]" data-testid="checkin-streak">
            <Flame size={16} weight="fill" /> {data.streak} day{data.streak > 1 ? "s" : ""}
          </div>
        )}
      </div>

      <p className="font-heading text-xl sm:text-2xl font-medium text-[#2C362B] mb-5 leading-snug" data-testid="checkin-prompt">
        {data.prompt}
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        {data.moods.map((m) => {
          const active = data.today_mood === m.key;
          return (
            <button
              key={m.key}
              disabled={saving}
              onClick={() => pick(m.key)}
              data-testid={`mood-${m.key}-btn`}
              className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-300 hover:scale-105 active:scale-95 border ${
                active ? "bg-[#8A9A86] border-[#8A9A86] text-white" : "bg-white border-[#E6EBE4] text-[#515A4F] hover:border-[#8A9A86]"
              }`}
            >
              <span className="text-base leading-none">{m.emoji}</span>
              {m.label}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => onPickPrompt && onPickPrompt(data.prompt)}
        className="mt-5 text-sm font-bold text-[#5E6C5C] hover:underline"
        data-testid="checkin-write-btn"
      >
        Write about it →
      </button>
    </motion.section>
  );
};
